import { defineStore } from 'pinia'
import AuthAPI from '@/api/auth.api'
import UserAPI from '@/api/user.api'
import { MFA_STAGES, CognitoGroups } from '@/stores/auth.constants'

const LOCAL_STORAGE_AUTHORIZED_USER_DETAILS_KEY = 'authenticatedUserDetails'

const MILLISECONDS_PER_MINUTES = 1000 * 60

const initialAuthData = {
  accessToken: '',
  refreshToken: '',
  idToken: '',
  cognitoUsername: '',
  email: '',
  name: '',
  authTime: ''
}

function _getInitialAuthorizationUserDetails() {
  try {
    return JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_AUTHORIZED_USER_DETAILS_KEY)
    )
  } catch (error) {
    console.error(error)

    return initialAuthData
  }
}

function _saveAuthenticatedUserDataToLocalStorage(authData) {
  try {
    localStorage.setItem(
      LOCAL_STORAGE_AUTHORIZED_USER_DETAILS_KEY,
      JSON.stringify(authData)
    )
  } catch (error) {
    console.error(error)
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    authenticatedUserDetails: _getInitialAuthorizationUserDetails(),

    redirectToAfterLoginPath: '',

    loginStage: {
      cognitoUsername: '',
      sessionID: '',
      currentAuthChallenge: MFA_STAGES.ENTER_USER_CREDS,
      email: ''
    },

    loginError: {
      errorMessage: '',
      errorCode: null
    },

    userDetails: {
      name: '',
      email: '',
      id: '' // this is the cognito username
    },

    MFASecretKey: '',

    accessTokenIntervalTimer: null
  }),

  getters: {
    isAuthenticated(state) {
      return !!state.authenticatedUserDetails?.accessToken
    },

    accessToken(state) {
      return state.authenticatedUserDetails?.accessToken
    },

    currentAuthChallenge(state) {
      return state.loginStage?.currentAuthChallenge
    },

    haveUserData(state) {
      return !!state.userDetails?.id
    },

    loggedInUserID(state) {
      return state.userDetails?.id
    },

    currentAccountManagerId(state) {
      return state.userDetails?.accountManagerId
    },

    isAccountManager(state) {
      return state.userDetails?.groups?.includes(CognitoGroups.ACCOUNT_MANAGER)
    },

    isAccountManagerAdmin(state) {
      return state.userDetails?.groups?.includes(CognitoGroups.ACCOUNT_MANAGER_ADMIN)
    },

    isFinancialManager(state) {
      return state.userDetails?.groups?.includes(CognitoGroups.FINANCIAL_MANAGER)
    },

    isSuperAdmin(state) {
      return state.userDetails?.groups?.includes(CognitoGroups.ADMIN)
    },

    isUserLoggedInWithinLastXMinutes: (state) => (mins) => {
      const authTime = state.authenticatedUserDetails?.authTime

      if (!authTime) {
        return false
      }

      const authDateTime = new Date(authTime)

      const diffInMilliseconds = Math.abs(new Date() - authDateTime)

      const diffInMins = Math.floor((diffInMilliseconds/1000)/60)

      if (diffInMins >= mins) {
        return false
      }

      return true
    },

    isMFASetup(state) {
      return Boolean(state.userDetails?.mfaSettings?.enabledMFA?.length)
    }
  },

  actions: {
    resetAuthData() {
      this.RESET_AUTHENTICATED_USER_DATA()
    },

    setRedirectToPathAfterLogin(path) {
      this.SET_REDIRECT_TO_AFTER_LOGIN_PATH(path)
    },

    async login({ email, password }) {
      try {
        this.RESET_LOGIN_ERROR()

        const response = await AuthAPI.login({ email, password })

        const nextStageData = response.data

        this.SET_LOGIN_STAGE_STATE({
          cognitoUsername: nextStageData.username,
          sessionID: nextStageData.sessionID,
          currentAuthChallenge: nextStageData.challengeName,
          email
        })

        if (nextStageData.authenticationResult) {
          this.setAuthData({ authData: nextStageData.authenticationResult })
        }
      } catch (error) {
        this.SET_LOGIN_ERROR({
          errorMessage: error.title,
          errorCode: error.errorCode
        })
      }
    },

    async submitMFATypeSelected(mfaType) {
      try {
        this.RESET_LOGIN_ERROR()

        const response = await AuthAPI.respondToAuthChallenge({
          challengeName: this.loginStage.currentAuthChallenge,
          challengeResponseAnswer: mfaType,
          sessionID: this.loginStage.sessionID,
          username: this.loginStage.cognitoUsername
        })

        const nextStageData = response.data

        this.SET_LOGIN_STAGE_STATE({
          sessionID: nextStageData.sessionID,
          currentAuthChallenge: mfaType
        })
      } catch (error) {
        this.SET_LOGIN_ERROR({
          errorMessage: error.title,
          errorCode: error.errorCode
        })
      }
    },

    async submitMFACode(mfaCode) {
      try {
        this.RESET_LOGIN_ERROR()

        const response = await AuthAPI.respondToAuthChallenge({
          challengeName: this.loginStage.currentAuthChallenge,
          challengeResponseAnswer: mfaCode,
          sessionID: this.loginStage.sessionID,
          username: this.loginStage.cognitoUsername
        })

        const responseData = response.data

        if (responseData.authenticationResult) {
          this.setAuthData({ authData: responseData.authenticationResult })
        }
      } catch (error) {
        this.SET_LOGIN_ERROR({
          errorMessage: error.title,
          errorCode: error.errorCode
        })

        throw error
      }
    },

    /**
     * Once the access token expires, the expiry for which is 20 mins, we logout the user
     * We need to keep refreshing the access token before the token expires, as long as the user is on the dashboard
     */
    async refreshAccessToken() {
      const response = await AuthAPI.refreshAuthDataUsingRefreshToken({
        refreshToken: this.authenticatedUserDetails.refreshToken,
        cognitoUsername: this.authenticatedUserDetails.cognitoUsername
      })

      const authenticationResult = response.data

      if (authenticationResult) {
        this.SET_AUTH_TOKENS(authenticationResult)

        _saveAuthenticatedUserDataToLocalStorage(this.authenticatedUserDetails)
      }
    },

    logout() {
      this.RESET_AUTHENTICATED_USER_DATA()

      try {
        localStorage.clear()
      } catch (error) {
        console.error(error)
      }

      this.clearPollingForNewAccessToken()
    },

    resetLoginStageState() {
      this.RESET_LOGIN_STAGE_STATE()
    },

    async getLoggedInUserDetails() {
      try {
        const response = await UserAPI.getLoggedInUserDetails()

        const userDetails = response.data

        this.SET_USER_DETAILS(userDetails)

        this.SET_AUTHENTICATED_USER_DATA({
          cognitoUsername: userDetails.id,
          email: userDetails.email,
        })

        _saveAuthenticatedUserDataToLocalStorage(this.authenticatedUserDetails)
      } catch (error) {
        console.error(error)
      }
    },

    setAuthData({ authData }) {
      this.SET_AUTH_TOKENS(authData)

      this.SET_AUTHENTICATED_USER_DATA({
        cognitoUsername: this.loginStage?.cognitoUsername,
        email: this.loginStage?.email
      })

      this.SET_USER_AUTH_TIME(new Date())

      _saveAuthenticatedUserDataToLocalStorage(this.authenticatedUserDetails)

      this.getLoggedInUserDetails()

      this.RESET_LOGIN_STAGE_STATE()
    },

    async setPermanentPassword(newPassword) {
      this.RESET_LOGIN_ERROR()

      const response = await AuthAPI.respondToAuthChallenge({
        challengeName: this.loginStage.currentAuthChallenge,
        challengeResponseAnswer: newPassword,
        sessionID: this.loginStage.sessionID,
        username: this.loginStage.cognitoUsername
      })

      const responseData = response.data

      this.SET_LOGIN_STAGE_STATE({
        sessionID: responseData.sessionID,
        currentAuthChallenge: responseData.challengeName
      })

      if (responseData.authenticationResult) {
        this.setAuthData({ authData: responseData.authenticationResult })
      }
    },

    async setNewPassword({ email, newPassword, confirmationCode }) {
      return AuthAPI.setNewPassword({ email, newPassword, confirmationCode })
    },

    resetMFAKey() {
      this.RESET_MFA_KEY()
    },

    async associateSoftwareMFA() {
      try {
        const response = await AuthAPI.associateSoftwareMFA({
          accessToken: this.authenticatedUserDetails.accessToken
        })
        this.SET_MFA_KEY(response.data.secretKey)
        return response.data.secretKey
      } catch(error) {
        // Backend sends no specific error code, so a plain 400 is the only
        // signal that the session token went stale mid-association.
        if (error.response.status === 400) {
          await this.refreshAccessToken()
          return this.associateSoftwareMFA()
        }

        throw error
      }
    },

    async verifySoftwareMFA(softwareToken) {
      const response = await AuthAPI.verifySoftwareMFA({
        accessToken: this.authenticatedUserDetails.accessToken,
        softwareToken
      })

      await this.getLoggedInUserDetails()

      return response.data
    },

    /**
     * We're supposed to logout after certain period of inactivity, so as soon as the access token expires, we logout the user from the platform.
     * This will make sure we keep getting a new access token with the latest expiry, as long as the user is on the page.
     * https://app.shortcut.com/ringbax/story/10020/inactivity-logout
     */
    startPollingForNewAccessToken() {
      if (!this.accessTokenIntervalTimer) {
        this.refreshAccessToken()
        this.SET_ACCESS_TOKEN_INTERVAL_TIMER(() => this.refreshAccessToken())
      }
    },

    clearPollingForNewAccessToken() {
      this.CLEAR_ACCESS_TOKEN_INTERVAL_TIMER()
    },

    async forgotPassword(email) {
      return AuthAPI.forgotPassword({ email })
    },

    async resetMfa(email) {
      return AuthAPI.resetMfa({ email })
    },

    RESET_LOGIN_STAGE_STATE() {
      this.loginStage = {
        cognitoUsername: '',
        sessionID: '',
        currentAuthChallenge: MFA_STAGES.ENTER_USER_CREDS
      }
    },

    SET_REDIRECT_TO_AFTER_LOGIN_PATH(path) {
      this.redirectToAfterLoginPath = path
    },

    SET_LOGIN_STAGE_STATE(data) {
      this.loginStage = {
        ...this.loginStage,
        ...data
      }
    },

    RESET_LOGIN_ERROR() {
      this.loginError = {
        errorMessage: '',
        errorCode: null
      }
    },

    SET_LOGIN_ERROR({ errorCode, errorMessage }) {
      this.loginError = {
        errorMessage,
        errorCode
      }
    },

    SET_AUTH_TOKENS(authTokenData) {
      this.authenticatedUserDetails = {
        ...this.authenticatedUserDetails,
        accessToken: authTokenData.accessToken,
        refreshToken:
          authTokenData.refreshToken ||
          this.authenticatedUserDetails.refreshToken,
        idToken: authTokenData.idToken
      }
    },

    RESET_AUTHENTICATED_USER_DATA() {
      this.authenticatedUserDetails = {
        ...initialAuthData
      }
    },

    SET_AUTHENTICATED_USER_DATA({ email, cognitoUsername }) {
      this.authenticatedUserDetails = {
        ...this.authenticatedUserDetails,
        email,
        cognitoUsername,
      }
    },

    SET_USER_AUTH_TIME(authTime) {
      this.authenticatedUserDetails = {
        ...this.authenticatedUserDetails,
        authTime
      }
    },

    SET_USER_DETAILS(userDetails) {
      this.userDetails = userDetails
    },

    SET_MFA_KEY(secretKey) {
      this.MFASecretKey = secretKey
    },

    RESET_MFA_KEY() {
      this.MFASecretKey = ''
    },

    SET_ACCESS_TOKEN_INTERVAL_TIMER(callback) {
      this.accessTokenIntervalTimer = setInterval(() => {
        if (callback) {
          callback()
        }
      }, MILLISECONDS_PER_MINUTES * 2)
    },

    CLEAR_ACCESS_TOKEN_INTERVAL_TIMER() {
      if (this.accessTokenIntervalTimer) {
        clearInterval(this.accessTokenIntervalTimer)
        this.accessTokenIntervalTimer = null
      }
    }
  }
})

export {
  LOCAL_STORAGE_AUTHORIZED_USER_DETAILS_KEY
}
